import { useState, useEffect, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { PerformanceModelsAdminTab } from "../components/admin/PerformanceModelsAdminTab";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchAPI } from "../lib/utils";
import { getProgramDayCount } from "../lib/programVisibility";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../components/ui/tabs";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogTrigger,
} from "../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../components/ui/tooltip";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
	Shield,
	Users,
	UserCog,
	Building2,
	BarChart3,
	Plus,
	Edit,
	Trash2,
	Share2,
	CheckCircle,
	XCircle,
	Search,
	Download,
	Database,
	Bell,
	X,
	Brain,
	FileText,
	ToggleLeft,
	Target,
	GripVertical,
	FolderOpen,
	Link2,
	Filter,
	Activity,
	Crosshair,
	Timer,
	Settings,
	Save,
	RefreshCw,
	Upload,
} from "lucide-react";
import { toast } from "sonner";
import { CoachManagement } from "../components/CoachManagement";
import { SportsAdminTab } from "../components/admin/SportsAdminTab";
import { ShootingReferenceTab } from "../components/admin/ShootingReferenceTab";
import { CollaborationLibraryTab } from "../components/admin/CollaborationLibraryTab";
import { AthleteAssignmentsTab } from "../components/admin/AthleteAssignmentsTab";
import { AiUsageAdminTab } from "../components/admin/AiUsageAdminTab";

const ADMIN_NAV_SECTIONS = [
	{
		title: "NAVIGAZIONE",
		items: [
			{ value: "users", label: "Utenti", icon: Users },
			{ value: "coaches", label: "Gestione Coach", icon: UserCog },
			{ value: "assignments", label: "Assegnazioni Atleti", icon: UserCog },
			{
				value: "access-analytics",
				label: "Accessi & Utilizzo",
				icon: Activity,
			},
			{
				value: "notification-metrics",
				label: "Metriche Notifiche",
				icon: Bell,
			},
			{ value: "programs", label: "Programmi", icon: BarChart3 },
			{
				value: "collaboration-library",
				label: "Collaborazione Library",
				icon: Share2,
			},
		],
	},
	{
		title: "CONFIGURAZIONE",
		items: [
			{ value: "sports", label: "Discipline", icon: Activity },
			{ value: "shooting", label: "Tiro", icon: Crosshair },
			{
				value: "profilo-prestativo",
				label: "Profilo Prestativo",
				icon: Target,
			},
			{
				value: "exercise-categories",
				label: "Categorie Esercizi",
				icon: FolderOpen,
			},
			{ value: "load-reference", label: "Riferimenti %", icon: Filter },
			{ value: "benchmark", label: "Benchmark Endurance", icon: Timer },
		],
	},
	{
		title: "SISTEMA",
		items: [
			{ value: "ai-docs", label: "Documenti AI", icon: Brain },
			{ value: "backup", label: "Backup", icon: Database },
		],
	},
];

// Ogni tab della pagina: serve a validare `?tab=` (un valore sconosciuto
// apre il primo, come prima).
const ALL_ADMIN_TABS = ADMIN_NAV_SECTIONS.flatMap((section) =>
	section.items.map((item) => item.value),
);

const DEFAULT_AI_DOC_CATEGORIES = [
	{ value: "methodology", label: "Metodologia" },
	{ value: "programming", label: "Programmazione" },
	{ value: "nutrition", label: "Nutrizione" },
	{ value: "recovery", label: "Recupero" },
	{ value: "reference", label: "Riferimenti" },
	{ value: "other", label: "Altro" },
];

export default function AdminDashboard() {
	const { user } = useAuth();
	const { language, t } = useLanguage();
	// Solo l'admin pieno: chi non lo e' torna alla dashboard invece di vedere un
	// guscio pieno di 403 (B11 di docs/sport-config/ANALYSIS.md). L'admin di una
	// disciplina lavora dalla sezione coach «Sport e modelli» (/sport-config),
	// dove il modello di sistema per lui e' in sola lettura (D17).
	const isFullAdmin = !!user?.is_admin;
	const [searchParams] = useSearchParams();
	const requestedTab = searchParams.get("tab");
	const initialTab = ALL_ADMIN_TABS.includes(requestedTab)
		? requestedTab
		: ALL_ADMIN_TABS[0];
	const [stats, setStats] = useState(null);
	const [notificationMetrics, setNotificationMetrics] = useState(null);
	const [users, setUsers] = useState([]);
	const [teams, setTeams] = useState([]);
	const [programs, setPrograms] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [userRoleFilter, setUserRoleFilter] = useState("all");
	const [userSortBy, setUserSortBy] = useState("created_desc");
	const [isExporting, setIsExporting] = useState(false);
	const [isSendingReminders, setIsSendingReminders] = useState(false);
	const [pushEnabledUsers, setPushEnabledUsers] = useState([]);
	const [isPushUsersOpen, setIsPushUsersOpen] = useState(false);
	const [isLoadingPushUsers, setIsLoadingPushUsers] = useState(false);
	const [isSubscriptionDetailsOpen, setIsSubscriptionDetailsOpen] =
		useState(false);
	const [isLoadingSubscriptionDetails, setIsLoadingSubscriptionDetails] =
		useState(false);
	const [subscriptionDetails, setSubscriptionDetails] = useState(null);

	// AI Training Documents
	const [aiDocs, setAiDocs] = useState([]);
	const [authorizedCoachIds, setAuthorizedCoachIds] = useState([]);
	const [isAiDocDialogOpen, setIsAiDocDialogOpen] = useState(false);
	const [editingAiDoc, setEditingAiDoc] = useState(null);
	const [aiDocPdfFile, setAiDocPdfFile] = useState(null);
	const [aiDocSourceMode, setAiDocSourceMode] = useState("text");
	const [isSavingAiDoc, setIsSavingAiDoc] = useState(false);
	const [aiDocCategories, setAiDocCategories] = useState(
		DEFAULT_AI_DOC_CATEGORIES,
	);
	const [newAiDocCategory, setNewAiDocCategory] = useState("");
	const [isSavingAiDocCategories, setIsSavingAiDocCategories] = useState(false);
	const [aiDocForm, setAiDocForm] = useState({
		title: "",
		content: "",
		category: "methodology",
		is_active: true,
	});

	// Coach-Athlete assignments
	const [coachAssignments, setCoachAssignments] = useState({
		coaches: [],
		teams: [],
		unassigned_athletes: [],
	});

	// Dialogs
	const [isUserEditOpen, setIsUserEditOpen] = useState(false);
	const [isShareOpen, setIsShareOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedLinkedAccountId, setSelectedLinkedAccountId] =
		useState("none");
	const [selectedProgram, setSelectedProgram] = useState(null);

	// Libreria esercizi: serve al conteggio per categoria. Il profilo
	// prestativo ha ora il suo componente (components/admin/PerformanceModelsAdminTab.js).
	const [allExercises, setAllExercises] = useState([]);

	// Exercise Categories
	const [exerciseCategories, setExerciseCategories] = useState([]);
	const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
	const [editingCategory, setEditingCategory] = useState(null);
	const [categoryForm, setCategoryForm] = useState({
		value: "",
		label: "",
		order: 0,
	});
	const [loadReferenceExerciseIds, setLoadReferenceExerciseIds] = useState([]);
	const [loadReferenceSearch, setLoadReferenceSearch] = useState("");
	const [isSavingLoadReferenceConfig, setIsSavingLoadReferenceConfig] =
		useState(false);
	const [benchmarkConfig, setBenchmarkConfig] = useState({
		running: { exercise_ids: [], exercises: [] },
		bike: { exercise_ids: [], exercises: [] },
		swim: { exercise_ids: [], exercises: [] },
		erg: { exercise_ids: [], exercises: [] },
	});
	const [benchmarkActiveType, setBenchmarkActiveType] = useState("running");
	const [benchmarkSearch, setBenchmarkSearch] = useState("");
	const [isSavingBenchmark, setIsSavingBenchmark] = useState(false);
	const getIsoDateDaysAgo = (daysAgo) => {
		const d = new Date();
		d.setDate(d.getDate() - daysAgo);
		return d.toISOString().slice(0, 10);
	};
	const [accessAnalyticsFilters, setAccessAnalyticsFilters] = useState({
		start_date: getIsoDateDaysAgo(6),
		end_date: getIsoDateDaysAgo(0),
		role: "all",
		athlete_id: "all",
	});
	const [accessAnalytics, setAccessAnalytics] = useState(null);
	const [isAccessAnalyticsLoading, setIsAccessAnalyticsLoading] =
		useState(false);
	const [referenceRepairDays, setReferenceRepairDays] = useState("7");
	const [referenceRepairPreview, setReferenceRepairPreview] = useState(null);
	const [isReferenceRepairLoading, setIsReferenceRepairLoading] =
		useState(false);
	const [isReferenceRepairApplying, setIsReferenceRepairApplying] =
		useState(false);
	const [userPictureCleanupResult, setUserPictureCleanupResult] =
		useState(null);
	const [
		isUserPictureCleanupPreviewLoading,
		setIsUserPictureCleanupPreviewLoading,
	] = useState(false);
	const [isUserPictureCleanupApplying, setIsUserPictureCleanupApplying] =
		useState(false);
	const [referenceRepairProgramSearch, setReferenceRepairProgramSearch] =
		useState("");
	const [
		referenceRepairSelectedProgramIds,
		setReferenceRepairSelectedProgramIds,
	] = useState([]);
	const [referenceRepairIncludeLoadFix, setReferenceRepairIncludeLoadFix] =
		useState(true);
	const [
		referenceRepairIncludeLibraryRelink,
		setReferenceRepairIncludeLibraryRelink,
	] = useState(true);

	const formatLastAccessDateTime = (dateStr) => {
		if (!dateStr) return language === "en" ? "Never" : "Mai";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatRegistrationDate = (dateStr) => {
		if (!dateStr) return "-";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleDateString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		});
	};

	const loadData = async () => {
		if (!isFullAdmin) {
			setLoading(false);
			return;
		}
		try {
			const [
				statsData,
				notificationMetricsData,
				usersData,
				teamsData,
				programsData,
				assignmentsData,
				aiDocsData,
				aiDocsSettings,
				loadReferenceSettings,
				pushEnabledUsersData,
				allExData,
				exerciseCatsData,
				benchmarkData,
			] = await Promise.all([
				fetchAPI("/admin/stats"),
				fetchAPI("/admin/notification-metrics").catch(() => null),
				fetchAPI("/admin/users"),
				fetchAPI("/teams"),
				fetchAPI("/programs?scope=all&compact=true"),
				fetchAPI("/admin/coaches-with-athletes").catch(() => ({
					coaches: [],
					teams: [],
					unassigned_athletes: [],
				})),
				fetchAPI("/ai-training-docs").catch(() => []),
				fetchAPI("/ai-training-docs/settings").catch(() => ({
					authorized_coach_ids: [],
					categories: DEFAULT_AI_DOC_CATEGORIES,
				})),
				fetchAPI("/admin/load-reference-exercises").catch(() => ({
					exercise_ids: [],
				})),
				fetchAPI("/admin/notification-metrics/push-enabled-users").catch(
					() => [],
				),
				fetchAPI("/exercises?scope=all").catch(() => []),
				fetchAPI("/admin/exercise-categories").catch(() => []),
				fetchAPI("/admin/benchmark-exercises").catch(() => ({})),
			]);
			setStats(statsData);
			setNotificationMetrics(notificationMetricsData);
			setUsers(usersData);
			setTeams(teamsData);
			setPrograms(programsData);
			setCoachAssignments(assignmentsData);
			setAiDocs(Array.isArray(aiDocsData) ? aiDocsData : []);
			setAuthorizedCoachIds(aiDocsSettings?.authorized_coach_ids || []);
			setAiDocCategories(
				Array.isArray(aiDocsSettings?.categories) &&
					aiDocsSettings.categories.length
					? aiDocsSettings.categories
					: DEFAULT_AI_DOC_CATEGORIES,
			);
			setLoadReferenceExerciseIds(
				Array.isArray(loadReferenceSettings?.exercise_ids)
					? loadReferenceSettings.exercise_ids
					: [],
			);
			setPushEnabledUsers(
				Array.isArray(pushEnabledUsersData) ? pushEnabledUsersData : [],
			);
			setAllExercises(Array.isArray(allExData) ? allExData : []);
			setExerciseCategories(
				Array.isArray(exerciseCatsData) ? exerciseCatsData : [],
			);
			if (benchmarkData && typeof benchmarkData === "object") {
				setBenchmarkConfig({
					running: benchmarkData.running || { exercise_ids: [], exercises: [] },
					bike: benchmarkData.bike || { exercise_ids: [], exercises: [] },
					swim: benchmarkData.swim || { exercise_ids: [], exercises: [] },
					erg: benchmarkData.erg || { exercise_ids: [], exercises: [] },
				});
			}
		} catch (error) {
			toast.error("Errore nel caricamento dati admin");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isPushUsersOpen) return;

		let cancelled = false;
		setIsLoadingPushUsers(true);
		fetchAPI("/admin/notification-metrics/push-enabled-users")
			.then((data) => {
				if (cancelled) return;
				setPushEnabledUsers(Array.isArray(data) ? data : []);
			})
			.catch(() => {
				if (cancelled) return;
				setPushEnabledUsers([]);
			})
			.finally(() => {
				if (cancelled) return;
				setIsLoadingPushUsers(false);
			});

		return () => {
			cancelled = true;
		};
	}, [isPushUsersOpen]);

	useEffect(() => {
		if (!isSubscriptionDetailsOpen) return;

		let cancelled = false;
		setIsLoadingSubscriptionDetails(true);
		fetchAPI("/admin/notification-metrics/subscription-details")
			.then((data) => {
				if (cancelled) return;
				setSubscriptionDetails(data && typeof data === "object" ? data : null);
			})
			.catch(() => {
				if (cancelled) return;
				setSubscriptionDetails(null);
			})
			.finally(() => {
				if (cancelled) return;
				setIsLoadingSubscriptionDetails(false);
			});

		return () => {
			cancelled = true;
		};
	}, [isSubscriptionDetailsOpen]);

	const loadAccessAnalytics = async (overrideFilters = null) => {
		const effectiveFilters = overrideFilters || accessAnalyticsFilters;
		setIsAccessAnalyticsLoading(true);
		try {
			const params = new URLSearchParams();
			params.set("start_date", effectiveFilters.start_date);
			params.set("end_date", effectiveFilters.end_date);
			params.set("role", effectiveFilters.role || "all");
			if (
				effectiveFilters.athlete_id &&
				effectiveFilters.athlete_id !== "all"
			) {
				params.set("athlete_id", effectiveFilters.athlete_id);
			}
			const data = await fetchAPI(
				`/admin/access-analytics?${params.toString()}`,
			);
			setAccessAnalytics(data);
		} catch (error) {
			toast.error("Errore caricamento analytics accessi");
			console.error(error);
		} finally {
			setIsAccessAnalyticsLoading(false);
		}
	};

	useEffect(() => {
		// Solo l'admin pieno: per chiunque altro e' un 403 con toast d'errore.
		if (!isFullAdmin) return;
		loadAccessAnalytics();
		// Una volta all'apertura, come `loadData`: i filtri si riapplicano dal form.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const applyAccessAnalyticsFilters = (event) => {
		event.preventDefault();
		loadAccessAnalytics();
	};

	const handleUpdateUser = async () => {
		if (!selectedUser) return;

		try {
			const linkedAccountIds = Array.isArray(selectedUser.linked_account_ids)
				? selectedUser.linked_account_ids
				: [];
			await fetchAPI(`/admin/users/${selectedUser.user_id}`, {
				method: "PUT",
				body: JSON.stringify({
					role: selectedUser.role,
					coach_enabled: !!selectedUser.coach_enabled,
					team_id: selectedUser.team_id,
					is_active: selectedUser.is_active,
					linked_account_ids: linkedAccountIds,
				}),
			});
			toast.success("Utente aggiornato!");
			setIsUserEditOpen(false);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleToggleUserActive = async (userId, currentStatus) => {
		try {
			await fetchAPI(`/admin/users/${userId}`, {
				method: "PUT",
				body: JSON.stringify({ is_active: !currentStatus }),
			});
			toast.success(currentStatus ? "Utente disattivato" : "Utente attivato");
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handlePermanentDeleteUser = async (targetUser) => {
		if (!targetUser?.user_id) return;
		if (targetUser.is_admin) {
			toast.error("Gli utenti admin non possono essere eliminati");
			return;
		}

		const firstConfirm = window.confirm(
			`Eliminare DEFINITIVAMENTE l'utente ${targetUser.name || targetUser.email}? Questa azione non è reversibile.`,
		);
		if (!firstConfirm) return;

		const secondConfirm = window.confirm(
			"Conferma finale: verranno rimossi anche sessioni e riferimenti associati all'utente.",
		);
		if (!secondConfirm) return;

		try {
			await fetchAPI(`/admin/users/${targetUser.user_id}/permanent`, {
				method: "DELETE",
			});
			toast.success("Utente eliminato definitivamente");
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleShareProgram = async (targetCoachId) => {
		if (!selectedProgram || !targetCoachId) return;

		try {
			await fetchAPI(`/admin/programs/${selectedProgram.program_id}/share`, {
				method: "POST",
				body: JSON.stringify({ coach_id: targetCoachId }),
			});
			toast.success("Programma condiviso!");
			setIsShareOpen(false);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleExportDatabase = async () => {
		setIsExporting(true);
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-database/download`,
				{
					credentials: "include",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
					},
				},
			);

			if (!response.ok) throw new Error("Export failed");

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `kraveon_backup_${new Date().toISOString().split("T")[0]}.json`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success("Backup scaricato!");
		} catch (error) {
			toast.error("Errore export: " + error.message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleSendReminders = async () => {
		setIsSendingReminders(true);
		try {
			const result = await fetchAPI("/admin/send-workout-reminders", {
				method: "POST",
			});
			toast.success(result.message);
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsSendingReminders(false);
		}
	};

	// --- AI Training Docs handlers ---

	const handleSaveAiDoc = async () => {
		const isPdfUpload = !editingAiDoc && aiDocSourceMode === "pdf";
		if (
			!aiDocForm.title.trim() ||
			(isPdfUpload ? !aiDocPdfFile : !aiDocForm.content.trim())
		) {
			toast.error("Titolo e contenuto sono obbligatori");
			return;
		}
		setIsSavingAiDoc(true);
		try {
			if (editingAiDoc) {
				await fetchAPI(`/ai-training-docs/${editingAiDoc.document_id}`, {
					method: "PUT",
					body: JSON.stringify(aiDocForm),
				});
				toast.success("Documento aggiornato");
			} else if (isPdfUpload) {
				const payload = new FormData();
				payload.append("title", aiDocForm.title.trim());
				payload.append("category", aiDocForm.category);
				payload.append("is_active", String(aiDocForm.is_active));
				payload.append("file", aiDocPdfFile);
				await fetchAPI("/ai-training-docs/upload", {
					method: "POST",
					body: payload,
				});
				toast.success("Documento PDF creato");
			} else {
				await fetchAPI("/ai-training-docs", {
					method: "POST",
					body: JSON.stringify(aiDocForm),
				});
				toast.success("Documento creato");
			}
			setIsAiDocDialogOpen(false);
			setEditingAiDoc(null);
			setAiDocForm({
				title: "",
				content: "",
				category: "methodology",
				is_active: true,
			});
			setAiDocPdfFile(null);
			setAiDocSourceMode("text");
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsSavingAiDoc(false);
		}
	};

	const handleDeleteAiDoc = async (docId) => {
		if (!window.confirm("Eliminare questo documento formativo?")) return;
		try {
			await fetchAPI(`/ai-training-docs/${docId}`, { method: "DELETE" });
			toast.success("Documento eliminato");
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleToggleAiDocActive = async (doc) => {
		try {
			await fetchAPI(`/ai-training-docs/${doc.document_id}`, {
				method: "PUT",
				body: JSON.stringify({ is_active: !doc.is_active }),
			});
			toast.success(
				doc.is_active ? "Documento disattivato" : "Documento attivato",
			);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleAddAiDocCategory = () => {
		const label = newAiDocCategory.trim();
		const value = label
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "");
		if (!value) {
			toast.error(t("aiDocsCategoryInvalid"));
			return;
		}
		if (aiDocCategories.some((category) => category.value === value)) {
			toast.error(t("aiDocsCategoryDuplicate"));
			return;
		}
		setAiDocCategories((current) => [...current, { value, label }]);
		setNewAiDocCategory("");
	};

	const handleSaveAiDocCategories = async () => {
		if (aiDocCategories.some((category) => !category.label.trim())) {
			toast.error(t("aiDocsCategoryEmpty"));
			return;
		}
		setIsSavingAiDocCategories(true);
		try {
			await fetchAPI("/ai-training-docs/settings", {
				method: "PUT",
				body: JSON.stringify({ categories: aiDocCategories }),
			});
			toast.success(t("aiDocsCategorySaved"));
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsSavingAiDocCategories(false);
		}
	};

	const handleRemoveAiDocCategory = (value) => {
		if (value === "other") {
			toast.error(t("aiDocsCategoryRequired"));
			return;
		}
		const isUsed = aiDocs.some((doc) => doc.category === value);
		if (isUsed) {
			toast.error(t("aiDocsCategoryUsed"));
			return;
		}
		setAiDocCategories((current) =>
			current.filter((category) => category.value !== value),
		);
	};

	const handleChangeAiDocCategoryLabel = (value, label) => {
		setAiDocCategories((current) =>
			current.map((category) =>
				category.value === value ? { ...category, label } : category,
			),
		);
	};

	const handleToggleCoachAiAccess = async (coachId) => {
		const isCurrentlyAuthorized = authorizedCoachIds.includes(coachId);
		const newList = isCurrentlyAuthorized
			? authorizedCoachIds.filter((id) => id !== coachId)
			: [...authorizedCoachIds, coachId];
		try {
			await fetchAPI("/ai-training-docs/settings", {
				method: "PUT",
				body: JSON.stringify({ authorized_coach_ids: newList }),
			});
			setAuthorizedCoachIds(newList);
			toast.success(
				isCurrentlyAuthorized
					? "Accesso coach rimosso"
					: "Accesso coach abilitato",
			);
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const openEditAiDoc = (doc) => {
		setEditingAiDoc(doc);
		setAiDocPdfFile(null);
		setAiDocSourceMode("text");
		setAiDocForm({
			title: doc.title,
			content: doc.content,
			category: doc.category,
			is_active: doc.is_active,
		});
		setIsAiDocDialogOpen(true);
	};

	// ── Exercise Categories Handlers ──

	const handleSaveCategory = async () => {
		if (!categoryForm.value.trim() || !categoryForm.label.trim()) {
			toast.error("Valore e etichetta sono obbligatori");
			return;
		}
		try {
			if (editingCategory) {
				await fetchAPI(
					`/admin/exercise-categories/${editingCategory.category_id}`,
					{
						method: "PUT",
						body: JSON.stringify(categoryForm),
					},
				);
				toast.success("Categoria aggiornata");
			} else {
				await fetchAPI("/admin/exercise-categories", {
					method: "POST",
					body: JSON.stringify({
						...categoryForm,
						order: exerciseCategories.length,
					}),
				});
				toast.success("Categoria creata");
			}
			setIsCategoryDialogOpen(false);
			setEditingCategory(null);
			setCategoryForm({ value: "", label: "", order: 0 });
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleDeleteCategory = async (categoryId) => {
		if (!window.confirm("Eliminare questa categoria?")) return;
		try {
			await fetchAPI(`/admin/exercise-categories/${categoryId}`, {
				method: "DELETE",
			});
			toast.success("Categoria eliminata");
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const openEditCategory = (cat) => {
		setEditingCategory(cat);
		setCategoryForm({
			value: cat.value,
			label: cat.label,
			order: cat.order || 0,
		});
		setIsCategoryDialogOpen(true);
	};

	const openNewCategory = () => {
		setEditingCategory(null);
		setCategoryForm({ value: "", label: "", order: exerciseCategories.length });
		setIsCategoryDialogOpen(true);
	};

	const getExerciseCountForCategory = (categoryValue) => {
		return allExercises.filter((e) => e.category === categoryValue).length;
	};

	const openNewAiDoc = () => {
		setEditingAiDoc(null);
		setAiDocPdfFile(null);
		setAiDocSourceMode("text");
		setAiDocForm({
			title: "",
			content: "",
			category: "methodology",
			is_active: true,
		});
		setIsAiDocDialogOpen(true);
	};

	const agenticFeatureOptions = [
		{
			key: "generate_program_agent",
			label: "Generazione Programma Agentica",
		},
		{ key: "program_review_agent", label: "Program Review Agentico" },
		{ key: "athlete_analysis_agent", label: "Analisi Atleta Agentica" },
	];

	const filteredUsers = useMemo(() => {
		const search = searchQuery.toLowerCase();
		const matching = users.filter((u) => {
			const matchesSearch =
				u.name?.toLowerCase().includes(search) ||
				u.email?.toLowerCase().includes(search);

			if (!matchesSearch) return false;

			if (userRoleFilter === "admin") return !!u.is_admin;
			// Dual-role accounts (role "athlete" + coach_enabled) belong to both lists.
			if (userRoleFilter === "coach")
				return !u.is_admin && (u.role === "coach" || !!u.coach_enabled);
			if (userRoleFilter === "athlete") {
				return !u.is_admin && u.role === "athlete";
			}
			if (userRoleFilter === "dual") return !u.is_admin && !!u.is_dual_role;
			if (userRoleFilter === "inactive") return u.is_active === false;

			return true;
		});

		const timestampOf = (value) => {
			if (!value) return null;
			const ms = new Date(value).getTime();
			return Number.isNaN(ms) ? null : ms;
		};
		// Rows with no usable date (legacy accounts, athletes who never logged in)
		// always sink to the bottom, so "più recenti" never opens on a blank date.
		const byDate = (field, direction) => (a, b) => {
			const ta = timestampOf(a[field]);
			const tb = timestampOf(b[field]);
			if (ta === null && tb === null) return 0;
			if (ta === null) return 1;
			if (tb === null) return -1;
			return direction === "asc" ? ta - tb : tb - ta;
		};
		const byName = (direction) => (a, b) => {
			const comparison = (a.name || a.email || "").localeCompare(
				b.name || b.email || "",
				language === "en" ? "en" : "it",
				{ sensitivity: "base" },
			);
			return direction === "asc" ? comparison : -comparison;
		};
		const comparators = {
			created_desc: byDate("created_at", "desc"),
			created_asc: byDate("created_at", "asc"),
			access_desc: byDate("last_access_at", "desc"),
			access_asc: byDate("last_access_at", "asc"),
			name_asc: byName("asc"),
			name_desc: byName("desc"),
		};

		return matching.sort(comparators[userSortBy] || comparators.created_desc);
	}, [users, searchQuery, userRoleFilter, userSortBy, language]);

	const coaches = users.filter(
		(u) =>
			u.role === "coach" ||
			u.role === "admin" ||
			u.is_admin ||
			!!u.coach_enabled,
	);
	const athletes = users.filter((u) => u.role === "athlete");

	/**
	 * Lista coach per il tab Assegnazioni: /admin/coaches-with-athletes porta
	 * email, discipline e atleti gia assegnati (serve per far scegliere il
	 * coach giusto), ma i chip devono risolvere anche coach_id che quella
	 * risposta non contiene: da qui l'unione con la lista utenti.
	 */
	const assignmentCoaches = useMemo(() => {
		const byId = new Map();
		coaches.forEach((coach) => byId.set(coach.user_id, coach));
		(coachAssignments.coaches || []).forEach((coach) => {
			byId.set(coach.user_id, { ...byId.get(coach.user_id), ...coach });
		});
		return Array.from(byId.values());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [users, coachAssignments.coaches]);
	const usersById = useMemo(() => {
		const map = new Map();
		(users || []).forEach((u) => {
			if (u?.user_id) {
				map.set(u.user_id, u);
			}
		});
		return map;
	}, [users]);
	const linkableAccounts = useMemo(() => {
		if (!selectedUser?.user_id) return [];
		const alreadyLinked = new Set(selectedUser.linked_account_ids || []);
		return (users || []).filter((candidate) => {
			if (!candidate?.user_id) return false;
			if (candidate.user_id === selectedUser.user_id) return false;
			if (
				!candidate.is_admin &&
				!["athlete", "coach"].includes(candidate.role)
			) {
				return false;
			}
			if (alreadyLinked.has(candidate.user_id)) return false;
			return true;
		});
	}, [selectedUser, users]);
	const maxAccessByDay = useMemo(() => {
		const values = (accessAnalytics?.access_by_day || []).map(
			(item) => item?.count || 0,
		);
		return Math.max(1, ...values);
	}, [accessAnalytics]);
	const maxAccessByHour = useMemo(() => {
		const values = (accessAnalytics?.access_by_hour || []).map(
			(item) => item?.count || 0,
		);
		return Math.max(1, ...values);
	}, [accessAnalytics]);

	const filteredLoadReferenceExercises = (allExercises || [])
		.filter((exercise) => {
			if (!exercise?.exercise_id || !exercise?.name) return false;
			if (!loadReferenceSearch.trim()) return true;
			const query = loadReferenceSearch.toLowerCase();
			return (
				exercise.name.toLowerCase().includes(query) ||
				String(exercise.category || "")
					.toLowerCase()
					.includes(query)
			);
		})
		.sort((a, b) => a.name.localeCompare(b.name));
	const selectedLoadReferenceExercises = loadReferenceExerciseIds
		.map((exerciseId) =>
			(allExercises || []).find(
				(exercise) => exercise.exercise_id === exerciseId,
			),
		)
		.filter(Boolean);

	const handleToggleLoadReferenceExercise = (exerciseId) => {
		setLoadReferenceExerciseIds((prev) => {
			if (prev.includes(exerciseId)) {
				return prev.filter((id) => id !== exerciseId);
			}
			return [...prev, exerciseId];
		});
	};

	const handleSaveLoadReferenceExercises = async () => {
		setIsSavingLoadReferenceConfig(true);
		try {
			await fetchAPI("/admin/load-reference-exercises", {
				method: "PUT",
				body: JSON.stringify({ exercise_ids: loadReferenceExerciseIds }),
			});
			toast.success("Configurazione riferimenti % aggiornata");
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsSavingLoadReferenceConfig(false);
		}
	};

	const BENCHMARK_TYPE_LABELS = {
		running: "Running",
		bike: "Bike",
		swim: "Swim",
		erg: "Erg",
	};

	const filteredBenchmarkExercises = (allExercises || [])
		.filter((exercise) => {
			if (!exercise?.exercise_id || !exercise?.name) return false;
			if (exercise.category !== "endurance") return false;
			if (!benchmarkSearch.trim()) return true;
			const query = benchmarkSearch.toLowerCase();
			return exercise.name.toLowerCase().includes(query);
		})
		.sort((a, b) => a.name.localeCompare(b.name));

	const currentBenchmarkIds =
		benchmarkConfig[benchmarkActiveType]?.exercise_ids || [];

	const selectedBenchmarkExercises = currentBenchmarkIds
		.map((exerciseId) =>
			(allExercises || []).find(
				(exercise) => exercise.exercise_id === exerciseId,
			),
		)
		.filter(Boolean);

	const handleToggleBenchmarkExercise = (exerciseId) => {
		setBenchmarkConfig((prev) => {
			const current = prev[benchmarkActiveType]?.exercise_ids || [];
			const updated = current.includes(exerciseId)
				? current.filter((id) => id !== exerciseId)
				: [...current, exerciseId];
			return {
				...prev,
				[benchmarkActiveType]: {
					...prev[benchmarkActiveType],
					exercise_ids: updated,
				},
			};
		});
	};

	const handleSaveBenchmarkExercises = async () => {
		setIsSavingBenchmark(true);
		try {
			await fetchAPI("/admin/benchmark-exercises", {
				method: "PUT",
				body: JSON.stringify({
					exercise_type: benchmarkActiveType,
					exercise_ids: currentBenchmarkIds,
				}),
			});
			toast.success(
				`Benchmark ${BENCHMARK_TYPE_LABELS[benchmarkActiveType]} aggiornati`,
			);
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsSavingBenchmark(false);
		}
	};

	if (user && !isFullAdmin) {
		return <Navigate to="/dashboard" replace />;
	}

	if (loading) {
		return (
			<Layout>
				<div className="flex items-center justify-center h-96">
					<div className="spinner"></div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div
				className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
				data-testid="admin-dashboard"
			>
				<div className="mb-8">
					<h1 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3">
						<Shield className="w-8 h-8 text-volt" />
						Admin Dashboard
					</h1>
					<p className="text-muted-foreground">
						{language === "it" ? "Gestione piattaforma" : "Platform management"}
					</p>
				</div>

				{/* Stats Cards */}
				{stats && (
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm text-muted-foreground">
											Utenti Totali
										</p>
										<p className="text-2xl font-heading">{stats.total_users}</p>
									</div>
									<Users className="w-8 h-8 text-volt" />
								</div>
							</CardContent>
						</Card>
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm text-muted-foreground">Coach</p>
										<p className="text-2xl font-heading">
											{stats.total_coaches}
										</p>
									</div>
									<UserCog className="w-8 h-8 text-acid" />
								</div>
							</CardContent>
						</Card>
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm text-muted-foreground">Atleti</p>
										<p className="text-2xl font-heading">
											{stats.total_athletes}
										</p>
									</div>
									<Users className="w-8 h-8 text-blue-500" />
								</div>
							</CardContent>
						</Card>
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm text-muted-foreground">Coach Team</p>
										<p className="text-2xl font-heading">{stats.total_teams}</p>
									</div>
									<Building2 className="w-8 h-8 text-purple-500" />
								</div>
							</CardContent>
						</Card>
					</div>
				)}

				<Tabs
					defaultValue={initialTab}
					className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch lg:items-start"
				>
					<TabsList className="flex flex-col h-auto items-stretch justify-start bg-secondary rounded-sm p-2 gap-1 w-full lg:w-56 xl:w-64 shrink-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
						{ADMIN_NAV_SECTIONS.map((section, sectionIndex) => (
							<div
								key={section.title}
								className={
									sectionIndex > 0
										? "w-full border-t border-border pt-2 mt-1"
										: "w-full"
								}
							>
								<p className="label-tactical text-muted-foreground px-1 pb-1.5">
									{section.title}
								</p>
								<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1">
									{section.items.map((item) => {
										const Icon = item.icon;
										return (
											<TabsTrigger
												key={item.value}
												value={item.value}
												className="w-full min-w-0 h-auto min-h-9 justify-start text-left whitespace-normal leading-tight gap-2 px-2.5 py-2 rounded-sm data-[state=active]:bg-volt data-[state=active]:text-white"
											>
												<Icon className="w-4 h-4 shrink-0" />
												<span className="min-w-0 flex-1">{item.label}</span>
											</TabsTrigger>
										);
									})}
								</div>
							</div>
						))}
					</TabsList>
					<div className="flex-1 min-w-0">
						{/* Users Tab */}
						<TabsContent value="users">
							<Card className="bg-card border-border rounded-sm">
								<CardHeader>
									<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
										<CardTitle className="font-heading text-xl">
											Gestione Utenti
										</CardTitle>
										<div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full lg:w-auto">
											<div className="relative w-full lg:w-64">
												<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
												<Input
													placeholder="Cerca utenti..."
													value={searchQuery}
													onChange={(e) => setSearchQuery(e.target.value)}
													className="pl-10 bg-secondary border-transparent rounded-sm"
												/>
											</div>
											<Select
												value={userRoleFilter}
												onValueChange={setUserRoleFilter}
											>
												<SelectTrigger className="w-full sm:w-[150px] bg-secondary border-transparent rounded-sm">
													<SelectValue placeholder="Tipo utente" />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													<SelectItem value="all">Tutti</SelectItem>
													<SelectItem value="admin">Admin</SelectItem>
													<SelectItem value="coach">Coach</SelectItem>
													<SelectItem value="athlete">Atleta</SelectItem>
													<SelectItem value="dual">Atleta + Coach</SelectItem>
													<SelectItem value="inactive">Inattivi</SelectItem>
												</SelectContent>
											</Select>
											<Select value={userSortBy} onValueChange={setUserSortBy}>
												<SelectTrigger
													className="w-full sm:w-[220px] bg-secondary border-transparent rounded-sm"
													data-testid="user-sort-select"
												>
													<SelectValue placeholder="Ordina per" />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													<SelectItem value="created_desc">
														Registrazione: più recenti
													</SelectItem>
													<SelectItem value="created_asc">
														Registrazione: meno recenti
													</SelectItem>
													<SelectItem value="access_desc">
														Ultimo accesso: più recente
													</SelectItem>
													<SelectItem value="access_asc">
														Ultimo accesso: meno recente
													</SelectItem>
													<SelectItem value="name_asc">Nome: A-Z</SelectItem>
													<SelectItem value="name_desc">Nome: Z-A</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>
									<p className="text-xs text-muted-foreground">
										{filteredUsers.length}{" "}
										{language === "en"
											? filteredUsers.length === 1
												? "user"
												: "users"
											: filteredUsers.length === 1
												? "utente"
												: "utenti"}
									</p>
								</CardHeader>
								<CardContent>
									<ScrollArea className="h-[500px]">
										<div className="space-y-2">
											{filteredUsers.map((u) => {
												const isUserActive = u.is_active !== false;
												const linkedAccountIds = Array.isArray(
													u.linked_account_ids,
												)
													? u.linked_account_ids
													: [];
												const linkedUsers = linkedAccountIds
													.map((linkedId) => usersById.get(linkedId))
													.filter(Boolean);
												const hasLinkedAccounts = linkedUsers.length > 0;
												const linkedAccountsLabel = hasLinkedAccounts
													? `Collegato con: ${linkedUsers
															.map(
																(linkedUser) =>
																	linkedUser.name || linkedUser.email,
															)
															.join(", ")}`
													: "Nessun account collegato";
												return (
													<div
														key={u.user_id}
														className="flex flex-col gap-4 p-4 bg-secondary rounded-sm lg:flex-row lg:items-center lg:justify-between"
														data-testid={`user-row-${u.user_id}`}
													>
														<div className="flex items-start gap-4 min-w-0">
															<div
																className={`w-10 h-10 rounded-full flex items-center justify-center ${
																	u.is_admin
																		? "bg-volt/20 text-volt"
																		: u.role === "coach"
																			? "bg-acid/20 text-acid"
																			: "bg-blue-500/20 text-blue-500"
																}`}
															>
																{u.name?.charAt(0).toUpperCase()}
															</div>
															<div>
																<p className="font-medium flex items-center gap-2">
																	{u.name}
																	{u.is_admin && (
																		<Badge className="bg-volt/20 text-volt border-0 text-xs">
																			Admin
																		</Badge>
																	)}
																	{!isUserActive && (
																		<Badge
																			variant="destructive"
																			className="text-xs"
																		>
																			Inattivo
																		</Badge>
																	)}
																</p>
																<p className="text-sm text-muted-foreground">
																	{u.email}
																</p>
																<p className="text-xs text-muted-foreground/80">
																	{language === "en"
																		? "Registered"
																		: "Registrato"}
																	: {formatRegistrationDate(u.created_at)}
																</p>
																{u.role === "athlete" && (
																	<p className="text-xs text-muted-foreground/80">
																		{language === "en"
																			? "Last access"
																			: "Ultimo accesso"}
																		:{" "}
																		{formatLastAccessDateTime(u.last_access_at)}
																	</p>
																)}
															</div>
														</div>
														<div className="flex flex-wrap items-center gap-3 lg:justify-end">
															<Badge variant="outline" className="capitalize">
																{u.role}
															</Badge>
															{u.is_dual_role && (
																<Badge
																	className="bg-acid/20 text-acid border-0 text-xs"
																	data-testid={`user-dual-role-${u.user_id}`}
																>
																	+ Coach
																</Badge>
															)}
															{u.team_name && (
																<Badge className="bg-purple-500/20 text-purple-400 border-0">
																	{u.team_name}
																</Badge>
															)}
															<div className="flex items-center gap-2">
																<TooltipProvider delayDuration={90}>
																	<Tooltip>
																		<TooltipTrigger asChild>
																			<div
																				className={`h-8 w-8 rounded-sm border flex items-center justify-center ${
																					hasLinkedAccounts
																						? "border-acid/50 text-acid bg-acid/10"
																						: "border-border text-muted-foreground bg-background/30"
																				}`}
																				data-testid={`user-linked-status-${u.user_id}`}
																			>
																				<Link2 className="w-4 h-4" />
																			</div>
																		</TooltipTrigger>
																		<TooltipContent
																			side="top"
																			className="max-w-[300px]"
																		>
																			{linkedAccountsLabel}
																		</TooltipContent>
																	</Tooltip>

																	<Tooltip>
																		<TooltipTrigger asChild>
																			<span>
																				<Button
																					variant="ghost"
																					size="icon"
																					className="rounded-sm"
																					onClick={() => {
																						setSelectedUser({
																							...u,
																							linked_account_ids: Array.isArray(
																								u.linked_account_ids,
																							)
																								? u.linked_account_ids
																								: [],
																						});
																						setSelectedLinkedAccountId("none");
																						setIsUserEditOpen(true);
																					}}
																					disabled={u.is_admin}
																				>
																					<Edit className="w-4 h-4" />
																				</Button>
																			</span>
																		</TooltipTrigger>
																		<TooltipContent side="top">
																			{u.is_admin
																				? "Gli utenti admin non sono modificabili"
																				: "Modifica ruolo e team dell'utente"}
																		</TooltipContent>
																	</Tooltip>

																	<Tooltip>
																		<TooltipTrigger asChild>
																			<span>
																				<Button
																					variant="ghost"
																					size="icon"
																					className={`rounded-sm ${isUserActive ? "hover:text-red-500" : "hover:text-green-500"}`}
																					onClick={() =>
																						handleToggleUserActive(
																							u.user_id,
																							isUserActive,
																						)
																					}
																					disabled={u.is_admin}
																				>
																					{isUserActive ? (
																						<XCircle className="w-4 h-4" />
																					) : (
																						<CheckCircle className="w-4 h-4" />
																					)}
																				</Button>
																			</span>
																		</TooltipTrigger>
																		<TooltipContent side="top">
																			{u.is_admin
																				? "Gli utenti admin non possono essere disattivati"
																				: isUserActive
																					? "Disattiva l'utente"
																					: "Riattiva l'utente"}
																		</TooltipContent>
																	</Tooltip>

																	<Tooltip>
																		<TooltipTrigger asChild>
																			<span>
																				<Button
																					variant="ghost"
																					size="icon"
																					className="rounded-sm hover:text-blaze"
																					onClick={() =>
																						handlePermanentDeleteUser(u)
																					}
																					disabled={u.is_admin}
																				>
																					<Trash2 className="w-4 h-4" />
																				</Button>
																			</span>
																		</TooltipTrigger>
																		<TooltipContent side="top">
																			{u.is_admin
																				? "Gli utenti admin non possono essere eliminati"
																				: "Elimina definitivamente l'utente"}
																		</TooltipContent>
																	</Tooltip>
																</TooltipProvider>
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</ScrollArea>
								</CardContent>
							</Card>
						</TabsContent>

						{/* Coach Management Tab */}
						<TabsContent value="coaches" className="space-y-4">
							<CoachManagement onDataChanged={loadData} />
							{/* Chi usa chiave API e client MCP, e quanto. */}
							<AiUsageAdminTab />
						</TabsContent>

						{/* Programs Tab */}
						<TabsContent value="programs">
							<Card className="bg-card border-border rounded-sm">
								<CardHeader>
									<CardTitle className="font-heading text-xl">
										Programmi - Condivisione
									</CardTitle>
								</CardHeader>
								<CardContent>
									<ScrollArea className="h-[500px]">
										<div className="space-y-2">
											{programs.map((p) => (
												<div
													key={p.program_id}
													className="flex flex-col gap-4 p-4 bg-secondary rounded-sm lg:flex-row lg:items-center lg:justify-between"
												>
													<div>
														<p className="font-medium">{p.title}</p>
														<p className="text-sm text-muted-foreground">
															Coach: {p.coach_name} • {getProgramDayCount(p)}{" "}
															giorni • {p.athlete_ids?.length || 0} atleti
														</p>
													</div>
													<div className="flex items-center gap-2">
														<Badge variant="outline" className="capitalize">
															{p.type}
														</Badge>
														<Button
															variant="outline"
															size="sm"
															className="rounded-sm"
															onClick={() => {
																setSelectedProgram(p);
																setIsShareOpen(true);
															}}
														>
															<Share2 className="w-4 h-4 mr-1" />
															Condividi
														</Button>
													</div>
												</div>
											))}
										</div>
									</ScrollArea>
								</CardContent>
							</Card>
						</TabsContent>

						{/* Collaboration Library Tab */}
						<TabsContent value="collaboration-library">
							<CollaborationLibraryTab coaches={coaches} />
						</TabsContent>

						{/* Assignments Tab */}
						<TabsContent value="assignments">
							<AthleteAssignmentsTab
								athletes={athletes}
								coaches={assignmentCoaches}
								teams={coachAssignments.teams || []}
								onReload={loadData}
								language={language}
							/>
						</TabsContent>

						<TabsContent value="access-analytics">
							<div className="space-y-4">
								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<CardTitle className="font-heading text-xl flex items-center gap-2">
											<Activity className="w-5 h-5 text-volt" />
											Analytics Accessi
										</CardTitle>
									</CardHeader>
									<CardContent>
										<form
											onSubmit={applyAccessAnalyticsFilters}
											className="grid grid-cols-1 lg:grid-cols-5 gap-3"
										>
											<div className="space-y-1">
												<Label>Da</Label>
												<Input
													type="date"
													value={accessAnalyticsFilters.start_date}
													onChange={(e) =>
														setAccessAnalyticsFilters((prev) => ({
															...prev,
															start_date: e.target.value,
														}))
													}
													className="bg-secondary border-transparent rounded-sm"
												/>
											</div>
											<div className="space-y-1">
												<Label>A</Label>
												<Input
													type="date"
													value={accessAnalyticsFilters.end_date}
													onChange={(e) =>
														setAccessAnalyticsFilters((prev) => ({
															...prev,
															end_date: e.target.value,
														}))
													}
													className="bg-secondary border-transparent rounded-sm"
												/>
											</div>
											<div className="space-y-1">
												<Label>Ruolo</Label>
												<Select
													value={accessAnalyticsFilters.role}
													onValueChange={(value) =>
														setAccessAnalyticsFilters((prev) => ({
															...prev,
															role: value,
														}))
													}
												>
													<SelectTrigger className="bg-secondary border-transparent rounded-sm">
														<SelectValue placeholder="Tutti" />
													</SelectTrigger>
													<SelectContent className="bg-card border-border">
														<SelectItem value="all">Tutti</SelectItem>
														<SelectItem value="athlete">Atleti</SelectItem>
														<SelectItem value="coach">Coach</SelectItem>
													</SelectContent>
												</Select>
											</div>
											<div className="space-y-1">
												<Label>Atleta</Label>
												<Select
													value={accessAnalyticsFilters.athlete_id}
													onValueChange={(value) =>
														setAccessAnalyticsFilters((prev) => ({
															...prev,
															athlete_id: value,
														}))
													}
												>
													<SelectTrigger className="bg-secondary border-transparent rounded-sm">
														<SelectValue placeholder="Tutti gli atleti" />
													</SelectTrigger>
													<SelectContent className="bg-card border-border">
														<SelectItem value="all">
															Tutti gli atleti
														</SelectItem>
														{athletes.map((athlete) => (
															<SelectItem
																key={athlete.user_id}
																value={athlete.user_id}
															>
																{athlete.name || athlete.email}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
											<div className="flex items-end">
												<Button
													type="submit"
													disabled={isAccessAnalyticsLoading}
													className="w-full bg-volt hover:bg-volt/90 rounded-sm"
												>
													{isAccessAnalyticsLoading
														? "Caricamento..."
														: "Applica filtri"}
												</Button>
											</div>
										</form>
									</CardContent>
								</Card>

								<div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Accessi Totali
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.total_access_events || 0}
											</p>
										</CardContent>
									</Card>
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Utenti Attivi
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.unique_active_users || 0}
											</p>
										</CardContent>
									</Card>
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Media Giornaliera
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.avg_daily_accesses || 0}
											</p>
										</CardContent>
									</Card>
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Risultati Inseriti
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.total_results_created || 0}
											</p>
										</CardContent>
									</Card>
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Messaggi Inviati
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.total_messages_sent || 0}
											</p>
										</CardContent>
									</Card>
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="p-4">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Voci Diario
											</p>
											<p className="text-2xl font-heading">
												{accessAnalytics?.summary?.total_diary_entries || 0}
											</p>
										</CardContent>
									</Card>
								</div>

								<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
									<Card className="bg-card border-border rounded-sm">
										<CardHeader>
											<CardTitle className="font-heading text-base">
												Accessi per Giorno
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="space-y-2">
												{(accessAnalytics?.access_by_day || []).length ===
													0 && (
													<p className="text-sm text-muted-foreground">
														Nessun dato nel range selezionato
													</p>
												)}
												{(accessAnalytics?.access_by_day || []).map((item) => {
													const width = Math.max(
														4,
														Math.round(
															((item.count || 0) / maxAccessByDay) * 100,
														),
													);
													return (
														<div key={item.date}>
															<div className="flex justify-between text-xs mb-1 text-muted-foreground">
																<span>{item.date}</span>
																<span>{item.count || 0}</span>
															</div>
															<div className="h-2 bg-secondary rounded-sm overflow-hidden">
																<div
																	className="h-full bg-volt"
																	style={{ width: `${width}%` }}
																/>
															</div>
														</div>
													);
												})}
											</div>
										</CardContent>
									</Card>

									<Card className="bg-card border-border rounded-sm">
										<CardHeader>
											<CardTitle className="font-heading text-base">
												Accessi per Ora
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="grid grid-cols-6 gap-2">
												{(accessAnalytics?.access_by_hour || []).map((item) => {
													const barHeight = Math.max(
														8,
														Math.round(
															((item.count || 0) / maxAccessByHour) * 64,
														),
													);
													return (
														<div key={item.hour} className="text-center">
															<div className="h-16 flex items-end justify-center bg-secondary rounded-sm">
																<div
																	className="w-6 bg-acid rounded-sm"
																	style={{ height: `${barHeight}px` }}
																/>
															</div>
															<p className="text-[10px] text-muted-foreground mt-1">
																{String(item.hour).padStart(2, "0")}
															</p>
															<p className="text-[10px]">{item.count || 0}</p>
														</div>
													);
												})}
											</div>
										</CardContent>
									</Card>
								</div>

								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<CardTitle className="font-heading text-base">
											Top Utenti Attivi
										</CardTitle>
										<p className="text-xs text-muted-foreground">
											Fonte dati: {accessAnalytics?.data_source || "-"}
										</p>
									</CardHeader>
									<CardContent>
										<div className="space-y-2">
											{(accessAnalytics?.top_users || []).length === 0 && (
												<p className="text-sm text-muted-foreground">
													Nessun utente nel periodo selezionato
												</p>
											)}
											{(accessAnalytics?.top_users || []).map((row) => (
												<div
													key={row.user_id}
													className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 p-3 bg-secondary rounded-sm text-sm"
												>
													<div>
														<p className="font-medium">{row.name}</p>
														<p className="text-xs text-muted-foreground">
															{row.email}
														</p>
													</div>
													<div className="text-xs md:text-sm">
														<p className="text-muted-foreground">Accessi</p>
														<p className="font-medium">
															{row.access_count || 0}
														</p>
													</div>
													<div className="text-xs md:text-sm">
														<p className="text-muted-foreground">Risultati</p>
														<p className="font-medium">
															{row.results_created || 0}
														</p>
													</div>
													<div className="text-xs md:text-sm">
														<p className="text-muted-foreground">Messaggi</p>
														<p className="font-medium">
															{row.messages_sent || 0}
														</p>
													</div>
													<div className="text-xs md:text-sm">
														<p className="text-muted-foreground">Diario</p>
														<p className="font-medium">
															{row.diary_entries || 0}
														</p>
													</div>
												</div>
											))}
										</div>
									</CardContent>
								</Card>
							</div>
						</TabsContent>

						<TabsContent value="notification-metrics">
							<Card className="bg-card border-border rounded-sm">
								<CardHeader>
									<CardTitle className="font-heading text-xl flex items-center gap-2">
										<Bell className="w-5 h-5 text-volt" />
										Metriche Notifiche Push
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
										<div className="bg-secondary rounded-sm p-3">
											<div className="flex items-start justify-between gap-2">
												<div>
													<p className="text-xs text-muted-foreground uppercase tracking-wider">
														Utenti Push Attive
													</p>
													<p className="font-heading text-2xl">
														{notificationMetrics?.push_enabled_users || 0}
													</p>
													<p className="text-xs text-muted-foreground mt-1">
														Adozione {notificationMetrics?.adoption_rate || 0}%
													</p>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													className="rounded-sm text-xs text-volt hover:text-volt hover:bg-volt/10 px-2"
													onClick={() => setIsPushUsersOpen(true)}
												>
													Dettagli
												</Button>
											</div>
										</div>
										<div className="bg-secondary rounded-sm p-3">
											<div className="flex items-start justify-between gap-2">
												<div>
													<p className="text-xs text-muted-foreground uppercase tracking-wider">
														Subscription Attive
													</p>
													<p className="font-heading text-2xl">
														{notificationMetrics?.active_subscriptions || 0}
													</p>
													<p className="text-xs text-muted-foreground mt-1">
														Utenti con subscription:{" "}
														{notificationMetrics?.users_with_active_subscriptions ||
															0}
													</p>
													<p className="text-xs text-muted-foreground mt-1">
														Media per utente:{" "}
														{(() => {
															const total =
																notificationMetrics?.active_subscriptions || 0;
															const usersCount =
																notificationMetrics?.users_with_active_subscriptions ||
																0;
															if (!usersCount) return "0";
															return (total / usersCount).toFixed(2);
														})()}
													</p>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													className="rounded-sm text-xs text-volt hover:text-volt hover:bg-volt/10 px-2"
													onClick={() => setIsSubscriptionDetailsOpen(true)}
												>
													Dettagli
												</Button>
											</div>
										</div>
										<div className="bg-secondary rounded-sm p-3">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Messaggi Totali
											</p>
											<p className="font-heading text-2xl">
												{notificationMetrics?.total_messages_sent || 0}
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												Ultime 24h:{" "}
												{notificationMetrics?.messages_last_24h || 0}
											</p>
										</div>
										<div className="bg-secondary rounded-sm p-3">
											<p className="text-xs text-muted-foreground uppercase tracking-wider">
												Problemi Delivery
											</p>
											<p className="font-heading text-2xl">
												{notificationMetrics?.failing_subscriptions || 0}
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												Messaggi ultimi 7gg:{" "}
												{notificationMetrics?.messages_last_7d || 0}
											</p>
										</div>
									</div>
									<div className="rounded-sm border border-border p-3 text-sm text-muted-foreground">
										Push per nuovi messaggi attive:{" "}
										<span className="text-foreground font-medium">
											{notificationMetrics?.push_new_message_users || 0}
										</span>
									</div>
								</CardContent>
							</Card>

							<Dialog open={isPushUsersOpen} onOpenChange={setIsPushUsersOpen}>
								<DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] rounded-sm overflow-hidden flex flex-col">
									<DialogDescription className="hidden" />
									<DialogHeader>
										<DialogTitle className="font-heading text-xl flex items-center gap-2">
											<Users className="w-5 h-5 text-volt" />
											Utenti con push attive
										</DialogTitle>
									</DialogHeader>
									<div className="flex-1 min-h-0 overflow-hidden flex flex-col">
										<p className="text-sm text-muted-foreground pb-2">
											{notificationMetrics?.push_enabled_users || 0} utenti
											hanno le notifiche push abilitate.
										</p>
										<div className="flex-1 min-h-0 max-h-[60vh] overflow-y-auto pr-2">
											<div className="space-y-2 pr-4">
												{isLoadingPushUsers ? (
													<p className="text-sm text-muted-foreground">
														Caricamento utenti...
													</p>
												) : pushEnabledUsers.length > 0 ? (
													pushEnabledUsers.map((item) => (
														<div
															key={item.user_id}
															className="flex items-center justify-between gap-3 rounded-sm border border-border bg-secondary/60 p-3"
														>
															<div className="min-w-0">
																<p className="font-medium truncate">
																	{item.name}
																</p>
																<p className="text-xs text-muted-foreground truncate">
																	{item.email || item.user_id}
																</p>
															</div>
															<div className="flex flex-wrap items-center gap-2 shrink-0">
																<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0 text-xs">
																	Push attive
																</Badge>
																{item.push_new_message ? (
																	<Badge
																		variant="outline"
																		className="rounded-sm text-xs"
																	>
																		Nuovi messaggi
																	</Badge>
																) : null}
															</div>
														</div>
													))
												) : (
													<p className="text-sm text-muted-foreground">
														Nessun utente ha push attive.
													</p>
												)}
											</div>
										</div>
									</div>
								</DialogContent>
							</Dialog>

							<Dialog
								open={isSubscriptionDetailsOpen}
								onOpenChange={setIsSubscriptionDetailsOpen}
							>
								<DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] rounded-sm overflow-hidden flex flex-col">
									<DialogDescription className="hidden" />
									<DialogHeader>
										<DialogTitle className="font-heading text-xl flex items-center gap-2">
											<Link2 className="w-5 h-5 text-volt" />
											Dettaglio Subscription Attive
										</DialogTitle>
									</DialogHeader>
									<div className="space-y-4 flex-1 min-h-0 overflow-hidden">
										{isLoadingSubscriptionDetails ? (
											<p className="text-sm text-muted-foreground">
												Caricamento dettaglio subscription...
											</p>
										) : (
											<>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
													<div className="rounded-sm border border-border p-3 bg-secondary/40">
														<p className="text-xs text-muted-foreground uppercase tracking-wider">
															Spiegazione Rapida
														</p>
														<p className="text-sm mt-2 text-muted-foreground">
															Le subscription sono per dispositivo/browser, non
															per utente. Uno stesso utente puo avere piu
															subscription attive.
														</p>
													</div>
													<div className="rounded-sm border border-border p-3 bg-secondary/40">
														<p className="text-xs text-muted-foreground uppercase tracking-wider">
															Numeri Attuali
														</p>
														<p className="text-sm mt-2 text-muted-foreground">
															Subscription attive:{" "}
															<span className="text-foreground font-medium">
																{subscriptionDetails?.summary
																	?.active_subscriptions || 0}
															</span>
														</p>
														<p className="text-sm text-muted-foreground">
															Utenti unici con subscription:{" "}
															<span className="text-foreground font-medium">
																{subscriptionDetails?.summary
																	?.users_with_active_subscriptions || 0}
															</span>
														</p>
														<p className="text-sm text-muted-foreground">
															Subscription extra (multi-device):{" "}
															<span className="text-foreground font-medium">
																{subscriptionDetails?.summary
																	?.extra_subscriptions_from_multi_device || 0}
															</span>
														</p>
														<p className="text-sm text-muted-foreground">
															Utenti con subscription ma push disabilitato:{" "}
															<span className="text-foreground font-medium">
																{subscriptionDetails?.summary
																	?.users_with_active_but_push_disabled || 0}
															</span>
														</p>
													</div>
												</div>

												<div className="rounded-sm border border-border p-3">
													<p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
														Utenti con Subscription Attive
													</p>
													<div className="max-h-[46vh] overflow-y-auto pr-1 space-y-2">
														{Array.isArray(subscriptionDetails?.users) &&
														subscriptionDetails.users.length > 0 ? (
															subscriptionDetails.users.map((item) => (
																<div
																	key={`sub-${item.user_id}`}
																	className="rounded-sm border border-border bg-secondary/50 p-3 flex items-center justify-between gap-3"
																>
																	<div className="min-w-0">
																		<p className="font-medium truncate">
																			{item.name}
																		</p>
																		<p className="text-xs text-muted-foreground truncate">
																			{item.email || item.user_id}
																		</p>
																	</div>
																	<div className="flex flex-wrap items-center gap-2 shrink-0">
																		<Badge className="rounded-sm bg-volt/20 text-volt border-0 text-xs">
																			{item.active_subscriptions || 0}{" "}
																			subscription
																		</Badge>
																		{item.push_enabled ? (
																			<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0 text-xs">
																				Push attive
																			</Badge>
																		) : (
																			<Badge className="rounded-sm bg-amber-500/20 text-amber-400 border-0 text-xs">
																				Push disabilitato
																			</Badge>
																		)}
																		{item.has_delivery_errors ? (
																			<Badge className="rounded-sm bg-blaze/20 text-blaze border-0 text-xs">
																				Errore delivery
																			</Badge>
																		) : null}
																	</div>
																</div>
															))
														) : (
															<p className="text-sm text-muted-foreground">
																Nessuna subscription attiva trovata.
															</p>
														)}
													</div>
												</div>
											</>
										)}
									</div>
								</DialogContent>
							</Dialog>
						</TabsContent>

						{/* Backup Tab */}
						<TabsContent value="backup">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<CardTitle className="font-heading text-xl flex items-center gap-2">
											<Download className="w-5 h-5 text-volt" />
											Export Database
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Scarica un backup completo del database in formato JSON.
											Include utenti, programmi, esercizi, risultati e messaggi.
										</p>
										<div className="bg-secondary p-4 rounded-sm text-sm">
											<p className="font-medium mb-2">Contenuto export:</p>
											<ul className="text-muted-foreground space-y-1">
												<li>• {stats?.total_users || 0} utenti</li>
												<li>• {stats?.total_programs || 0} programmi</li>
												<li>• {stats?.total_exercises || 0} esercizi</li>
												<li>• {stats?.total_results || 0} risultati</li>
												<li>• {stats?.total_teams || 0} team</li>
												<li>• messaggi e documenti</li>
											</ul>
										</div>
										<Button
											onClick={handleExportDatabase}
											disabled={isExporting}
											className="w-full bg-volt hover:bg-volt/90 rounded-sm"
											data-testid="export-db-btn"
										>
											{isExporting ? (
												<>Esportazione in corso...</>
											) : (
												<>
													<Download className="w-4 h-4 mr-2" />
													Scarica Backup JSON
												</>
											)}
										</Button>
									</CardContent>
								</Card>

								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<CardTitle className="font-heading text-xl flex items-center gap-2">
											<Bell className="w-5 h-5 text-acid" />
											Notifiche
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Invia promemoria via email a tutti gli atleti che hanno un
											allenamento programmato per oggi.
										</p>
										<div className="bg-secondary p-4 rounded-sm">
											<p className="text-sm text-muted-foreground">
												Le email vengono inviate solo agli atleti che hanno
												attivato le notifiche nelle impostazioni.
											</p>
										</div>
										<Button
											onClick={handleSendReminders}
											disabled={isSendingReminders}
											variant="outline"
											className="w-full rounded-sm"
											data-testid="send-reminders-btn"
										>
											{isSendingReminders ? (
												<>Invio in corso...</>
											) : (
												<>
													<Bell className="w-4 h-4 mr-2" />
													Invia Promemoria Allenamenti
												</>
											)}
										</Button>
									</CardContent>
								</Card>
							</div>
						</TabsContent>

						{/* AI Training Documents Tab */}
						<TabsContent value="ai-docs">
							<div className="space-y-6">
								{/* Osservabilita' AI e runtime-config rimosse con il rework
								    BYOK+MCP: esistevano per governare una spesa che la
								    piattaforma non sostiene piu'. Il coach sceglie modello e
								    provider dal proprio account.
								    docs/ai-program/10-byok-and-mcp-rework.md */}
								{/* Per-coach access card */}
								<Card className="bg-card border-border rounded-sm">
									<CardHeader className="pb-3">
										<CardTitle className="font-heading text-lg flex items-center gap-2">
											<ToggleLeft className="w-5 h-5 text-volt" />
											Accesso Coach ai Documenti AI
										</CardTitle>
										<p className="text-sm text-muted-foreground">
											Abilita singolarmente i coach che possono creare e gestire
											documenti formativi
										</p>
									</CardHeader>
									<CardContent>
										{coaches.length > 0 ? (
											<div className="space-y-2">
												{coaches.map((coach) => (
													<div
														key={coach.user_id}
														className="flex items-center justify-between p-3 bg-secondary rounded-sm"
													>
														<div className="flex items-center gap-3">
															<div className="w-8 h-8 bg-acid/20 rounded-full flex items-center justify-center text-acid text-sm font-heading">
																{coach.name?.charAt(0).toUpperCase()}
															</div>
															<div>
																<p className="text-sm font-medium">
																	{coach.name}
																</p>
																<p className="text-xs text-muted-foreground">
																	{coach.email}
																</p>
															</div>
														</div>
														<Switch
															checked={authorizedCoachIds.includes(
																coach.user_id,
															)}
															onCheckedChange={() =>
																handleToggleCoachAiAccess(coach.user_id)
															}
														/>
													</div>
												))}
											</div>
										) : (
											<p className="text-sm text-muted-foreground text-center py-4">
												Nessun coach trovato
											</p>
										)}
									</CardContent>
								</Card>

								<Card className="bg-card border-border rounded-sm">
									<CardHeader className="pb-3">
										<div className="flex flex-wrap items-start justify-between gap-3">
											<div>
												<CardTitle className="font-heading text-lg flex items-center gap-2">
													<FolderOpen className="w-5 h-5 text-volt" />
													{t("aiDocsCategoriesTitle")}
												</CardTitle>
												<p className="mt-1 text-sm text-muted-foreground">
													{t("aiDocsCategoriesSubtitle")}
												</p>
											</div>
											<Button
												onClick={handleSaveAiDocCategories}
												disabled={isSavingAiDocCategories}
												className="bg-volt hover:bg-volt/90 rounded-sm"
											>
												<Save className="mr-2 h-4 w-4" />
												{isSavingAiDocCategories
													? t("aiDocsSaving")
													: t("aiDocsCategoriesSave")}
											</Button>
										</div>
									</CardHeader>
									<CardContent className="space-y-3">
										<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
											{aiDocCategories.map((category) => {
												const usageCount = aiDocs.filter(
													(doc) => doc.category === category.value,
												).length;
												const canRemove =
													category.value !== "other" && usageCount === 0;
												return (
													<div
														key={category.value}
														className="flex min-w-0 items-center gap-2 rounded-sm border border-border bg-secondary/50 p-2"
													>
														<div className="min-w-0 flex-1">
															<Input
																value={category.label}
																onChange={(event) =>
																	handleChangeAiDocCategoryLabel(
																		category.value,
																		event.target.value,
																	)
																}
																aria-label={`${t("aiDocsCategoryName")}: ${category.label}`}
																className="h-9 bg-background border-border rounded-sm"
															/>
															<p className="px-1 pt-1 text-[10px] text-muted-foreground">
																{category.value} · {usageCount}{" "}
																{t("aiDocsCategoryUsage")}
															</p>
														</div>
														<TooltipProvider delayDuration={150}>
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant="ghost"
																		size="icon"
																		className={`h-9 w-9 shrink-0 rounded-sm text-blaze hover:bg-blaze/10 hover:text-blaze ${canRemove ? "" : "opacity-40"}`}
																		aria-disabled={!canRemove}
																		onClick={() =>
																			handleRemoveAiDocCategory(category.value)
																		}
																		aria-label={`Elimina categoria ${category.label}`}
																	>
																		<Trash2 className="h-4 w-4" />
																	</Button>
																</TooltipTrigger>
																<TooltipContent>
																	{category.value === "other"
																		? t("aiDocsCategoryRequired")
																		: usageCount
																			? t("aiDocsCategoryUsed")
																			: t("aiDocsCategoryRemove")}
																</TooltipContent>
															</Tooltip>
														</TooltipProvider>
													</div>
												);
											})}
										</div>
										<div className="flex flex-col gap-2 sm:flex-row">
											<Input
												value={newAiDocCategory}
												onChange={(event) =>
													setNewAiDocCategory(event.target.value)
												}
												onKeyDown={(event) => {
													if (event.key === "Enter") {
														event.preventDefault();
														handleAddAiDocCategory();
													}
												}}
												placeholder={t("aiDocsCategoryAddPlaceholder")}
												className="bg-secondary border-border rounded-sm"
											/>
											<Button
												onClick={handleAddAiDocCategory}
												variant="outline"
												className="rounded-sm sm:shrink-0"
											>
												<Plus className="mr-2 h-4 w-4" />
												{t("aiDocsCategoryAdd")}
											</Button>
										</div>
									</CardContent>
								</Card>

								{/* Documents list */}
								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<div className="flex items-center justify-between">
											<div>
												<CardTitle className="font-heading text-xl flex items-center gap-2">
													<Brain className="w-5 h-5 text-volt" />
													Documenti Formativi AI
												</CardTitle>
												<p className="text-sm text-muted-foreground mt-1">
													Questi documenti vengono usati come contesto dall'AI
													per generare programmi migliori
												</p>
											</div>
											<Button
												onClick={openNewAiDoc}
												className="bg-volt hover:bg-volt/90 rounded-sm"
											>
												<Plus className="w-4 h-4 mr-2" />
												Nuovo Documento
											</Button>
										</div>
									</CardHeader>
									<CardContent>
										{aiDocs.length > 0 ? (
											<ScrollArea className="h-[500px]">
												<div className="space-y-3">
													{aiDocs.map((doc) => (
														<div
															key={doc.document_id}
															className="flex flex-col gap-3 p-4 bg-secondary rounded-sm"
														>
															<div className="min-w-0">
																<div className="flex items-center gap-2 mb-1">
																	<FileText className="w-4 h-4 text-volt shrink-0" />
																	<p className="font-medium truncate">
																		{doc.title}
																	</p>
																	<Badge
																		variant="outline"
																		className="capitalize text-xs shrink-0"
																	>
																		{aiDocCategories.find(
																			(c) => c.value === doc.category,
																		)?.label || doc.category}
																	</Badge>
																	{doc.source_type === "pdf" && (
																		<Badge
																			variant="outline"
																			className="text-xs shrink-0"
																		>
																			PDF
																			{doc.page_count
																				? ` · ${doc.page_count} pagine`
																				: ""}
																		</Badge>
																	)}
																	{doc.is_active ? (
																		<Badge className="bg-green-500/20 text-green-400 border-0 text-xs shrink-0">
																			Attivo
																		</Badge>
																	) : (
																		<Badge className="bg-red-500/20 text-red-400 border-0 text-xs shrink-0">
																			Disattivato
																		</Badge>
																	)}
																</div>
																<p className="text-sm text-muted-foreground line-clamp-2">
																	{doc.content}
																</p>
																{doc.original_filename && (
																	<p className="mt-1 text-xs text-muted-foreground">
																		{doc.original_filename}
																	</p>
																)}
																{(() => {
																	const len =
																		doc.content_length ??
																		(doc.content?.length || 0);
																	const max = doc.source_max_chars || 12000;
																	const over = len > max;
																	return (
																		<p
																			className={`mt-1.5 text-[11px] font-mono ${over ? "text-amber-400" : "text-muted-foreground/70"}`}
																		>
																			{len.toLocaleString()} /{" "}
																			{max.toLocaleString()} caratteri
																			{over
																				? " · ⚠ troppo grande: troncata, valuta di splittarla"
																				: " · entra intera"}
																		</p>
																	);
																})()}
															</div>
															<div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-2">
																<TooltipProvider delayDuration={150}>
																	<Tooltip>
																		<TooltipTrigger asChild>
																			<Button
																				variant="ghost"
																				size="icon"
																				className="rounded-sm"
																				onClick={() =>
																					handleToggleAiDocActive(doc)
																				}
																				title={
																					doc.is_active ? "Disattiva" : "Attiva"
																				}
																			>
																				{doc.is_active ? (
																					<XCircle className="w-4 h-4 text-muted-foreground" />
																				) : (
																					<CheckCircle className="w-4 h-4 text-green-500" />
																				)}
																			</Button>
																		</TooltipTrigger>
																		<TooltipContent>
																			{doc.is_active
																				? t("aiDocsDeactivateHelp")
																				: t("aiDocsActivateHelp")}
																		</TooltipContent>
																	</Tooltip>
																	<Tooltip>
																		<TooltipTrigger asChild>
																			<Button
																				variant="ghost"
																				size="icon"
																				className="rounded-sm"
																				onClick={() => openEditAiDoc(doc)}
																			>
																				<Edit className="w-4 h-4" />
																			</Button>
																		</TooltipTrigger>
																		<TooltipContent>
																			{t("aiDocsEditHelp")}
																		</TooltipContent>
																	</Tooltip>
																	<Tooltip>
																		<TooltipTrigger asChild>
																			<Button
																				variant="ghost"
																				size="sm"
																				className="h-9 rounded-sm border border-blaze/40 px-3 text-blaze hover:bg-blaze/10 hover:text-blaze"
																				onClick={() =>
																					handleDeleteAiDoc(doc.document_id)
																				}
																				title="Elimina documento"
																			>
																				<Trash2 className="w-4 h-4" />
																				<span>Elimina documento</span>
																			</Button>
																		</TooltipTrigger>
																		<TooltipContent>
																			{t("aiDocsDeleteHelp")}
																		</TooltipContent>
																	</Tooltip>
																</TooltipProvider>
															</div>
														</div>
													))}
												</div>
											</ScrollArea>
										) : (
											<div className="text-center py-12 text-muted-foreground">
												<Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
												<p className="font-heading text-lg mb-2">
													Nessun documento formativo
												</p>
												<p className="text-sm">
													Crea documenti con linee guida, metodologie e
													riferimenti per dare contesto all'AI
												</p>
											</div>
										)}
									</CardContent>
								</Card>
							</div>
						</TabsContent>

						<TabsContent value="load-reference">
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<div className="flex items-center justify-between gap-3">
											<div>
												<CardTitle className="font-heading text-xl flex items-center gap-2">
													<Filter className="w-5 h-5 text-volt" />
													Esercizi Abilitati al Riferimento %
												</CardTitle>
												<p className="text-sm text-muted-foreground mt-1">
													Questa lista sarà usata in tutte le modali in cui
													scegli l'esercizio di riferimento per il carico %.
												</p>
											</div>
											<Button
												onClick={handleSaveLoadReferenceExercises}
												disabled={isSavingLoadReferenceConfig}
												className="bg-volt hover:bg-volt/90 rounded-sm"
											>
												<CheckCircle className="w-4 h-4 mr-2" />
												{isSavingLoadReferenceConfig
													? "Salvataggio..."
													: "Salva"}
											</Button>
										</div>
									</CardHeader>
									<CardContent>
										{selectedLoadReferenceExercises.length > 0 ? (
											<div className="flex flex-wrap gap-2">
												{selectedLoadReferenceExercises.map((exercise) => (
													<Badge
														key={exercise.exercise_id}
														className="bg-volt/15 text-volt border border-volt/40 rounded-sm px-2 py-1"
													>
														{exercise.name}
														<button
															type="button"
															onClick={() =>
																handleToggleLoadReferenceExercise(
																	exercise.exercise_id,
																)
															}
															className="ml-2 text-volt/90 hover:text-blaze"
														>
															<X className="w-3 h-3" />
														</button>
													</Badge>
												))}
											</div>
										) : (
											<p className="text-sm text-muted-foreground">
												Nessun esercizio selezionato: se lasci vuoto, il sistema
												userà tutta la libreria.
											</p>
										)}
									</CardContent>
								</Card>

								<Card className="bg-card border-border rounded-sm">
									<CardHeader>
										<CardTitle className="font-heading text-xl">
											Libreria Esercizi
										</CardTitle>
										<Input
											placeholder="Cerca esercizio o categoria..."
											value={loadReferenceSearch}
											onChange={(e) => setLoadReferenceSearch(e.target.value)}
											className="bg-secondary border-transparent rounded-sm"
										/>
									</CardHeader>
									<CardContent>
										<ScrollArea className="h-[420px] pr-1">
											<div className="space-y-2">
												{filteredLoadReferenceExercises.map((exercise) => {
													const isSelected = loadReferenceExerciseIds.includes(
														exercise.exercise_id,
													);
													return (
														<button
															type="button"
															key={exercise.exercise_id}
															onClick={() =>
																handleToggleLoadReferenceExercise(
																	exercise.exercise_id,
																)
															}
															className={`w-full p-3 rounded-sm border text-left flex items-center justify-between gap-3 ${
																isSelected
																	? "bg-volt/10 border-volt/60"
																	: "bg-secondary border-border"
															}`}
														>
															<div className="min-w-0">
																<p className="font-medium truncate">
																	{exercise.name}
																</p>
																<p className="text-xs text-muted-foreground truncate">
																	{exercise.category || "-"}
																</p>
															</div>
															{isSelected ? (
																<CheckCircle className="w-4 h-4 text-volt shrink-0" />
															) : (
																<Plus className="w-4 h-4 text-muted-foreground shrink-0" />
															)}
														</button>
													);
												})}
												{filteredLoadReferenceExercises.length === 0 && (
													<p className="text-sm text-muted-foreground py-8 text-center">
														Nessun esercizio trovato.
													</p>
												)}
											</div>
										</ScrollArea>
									</CardContent>
								</Card>
							</div>
						</TabsContent>

						{/* Benchmark Endurance Tab */}
						<TabsContent value="benchmark">
							<div className="space-y-4">
								{/* Type selector */}
								<div className="flex items-center gap-3">
									{Object.entries(BENCHMARK_TYPE_LABELS).map(
										([typeKey, typeLabel]) => (
											<button
												key={typeKey}
												onClick={() => {
													setBenchmarkActiveType(typeKey);
													setBenchmarkSearch("");
												}}
												className={`px-4 py-2 rounded-sm border text-sm font-medium transition-colors ${
													benchmarkActiveType === typeKey
														? "border-volt bg-volt/10 text-volt"
														: "border-border text-muted-foreground hover:border-volt/50"
												}`}
											>
												{typeLabel}
											</button>
										),
									)}
								</div>

								<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
									{/* Selected benchmarks */}
									<Card className="bg-card border-border rounded-sm">
										<CardHeader>
											<div className="flex items-center justify-between gap-3">
												<div>
													<CardTitle className="font-heading text-xl flex items-center gap-2">
														<Timer className="w-5 h-5 text-volt" />
														Benchmark{" "}
														{BENCHMARK_TYPE_LABELS[benchmarkActiveType]}
													</CardTitle>
													<p className="text-sm text-muted-foreground mt-1">
														Esercizi di riferimento per il pacing nel{" "}
														{BENCHMARK_TYPE_LABELS[
															benchmarkActiveType
														].toLowerCase()}
														. Gli atleti potranno ricevere prescrizioni basate
														sulla % del passo del benchmark.
													</p>
												</div>
												<Button
													onClick={handleSaveBenchmarkExercises}
													disabled={isSavingBenchmark}
													className="bg-volt hover:bg-volt/90 rounded-sm"
												>
													<CheckCircle className="w-4 h-4 mr-2" />
													{isSavingBenchmark ? "Salvataggio..." : "Salva"}
												</Button>
											</div>
										</CardHeader>
										<CardContent>
											{selectedBenchmarkExercises.length > 0 ? (
												<div className="flex flex-wrap gap-2">
													{selectedBenchmarkExercises.map((exercise) => (
														<Badge
															key={exercise.exercise_id}
															className="bg-volt/15 text-volt border border-volt/40 rounded-sm px-2 py-1"
														>
															{exercise.name}
															<button
																type="button"
																onClick={() =>
																	handleToggleBenchmarkExercise(
																		exercise.exercise_id,
																	)
																}
																className="ml-2 text-volt/90 hover:text-blaze"
															>
																<X className="w-3 h-3" />
															</button>
														</Badge>
													))}
												</div>
											) : (
												<p className="text-sm text-muted-foreground">
													Nessun benchmark selezionato per{" "}
													{BENCHMARK_TYPE_LABELS[
														benchmarkActiveType
													].toLowerCase()}
													. Seleziona esercizi dalla libreria (categoria
													"endurance").
												</p>
											)}
										</CardContent>
									</Card>

									{/* Exercise picker (endurance only) */}
									<Card className="bg-card border-border rounded-sm">
										<CardHeader>
											<CardTitle className="font-heading text-xl">
												Libreria Esercizi (Endurance)
											</CardTitle>
											<Input
												placeholder="Cerca esercizio endurance..."
												value={benchmarkSearch}
												onChange={(e) => setBenchmarkSearch(e.target.value)}
												className="bg-secondary border-transparent rounded-sm"
											/>
										</CardHeader>
										<CardContent>
											<ScrollArea className="h-[420px] pr-1">
												<div className="space-y-2">
													{filteredBenchmarkExercises.map((exercise) => {
														const isSelected = currentBenchmarkIds.includes(
															exercise.exercise_id,
														);
														return (
															<button
																type="button"
																key={exercise.exercise_id}
																onClick={() =>
																	handleToggleBenchmarkExercise(
																		exercise.exercise_id,
																	)
																}
																className={`w-full p-3 rounded-sm border text-left flex items-center justify-between gap-3 ${
																	isSelected
																		? "bg-volt/10 border-volt/60"
																		: "bg-secondary border-border"
																}`}
															>
																<div className="min-w-0">
																	<p className="font-medium truncate">
																		{exercise.name}
																	</p>
																	<p className="text-xs text-muted-foreground truncate">
																		endurance
																	</p>
																</div>
																{isSelected ? (
																	<CheckCircle className="w-4 h-4 text-volt shrink-0" />
																) : (
																	<Plus className="w-4 h-4 text-muted-foreground shrink-0" />
																)}
															</button>
														);
													})}
													{filteredBenchmarkExercises.length === 0 && (
														<p className="text-sm text-muted-foreground py-8 text-center">
															{benchmarkSearch
																? "Nessun esercizio trovato."
																: 'Nessun esercizio con categoria "endurance" in libreria. Crea esercizi endurance nella libreria per poterli usare come benchmark.'}
														</p>
													)}
												</div>
											</ScrollArea>
										</CardContent>
									</Card>
								</div>
							</div>
						</TabsContent>

						{/* Shooting reference data: division, classi, sistemi di punteggio.
						    Pannello a sé (components/admin/) — AdminDashboard.js è già
						    a ~240K e non regge altra UI inline. */}
						<TabsContent value="shooting">
							<ShootingReferenceTab />
						</TabsContent>

						{/* Discipline: il catalogo degli sport, globali e dei coach
						    (docs/sport-config, Fase 3). Carica i suoi dati da sé. */}
						<TabsContent value="sports">
							<SportsAdminTab />
						</TabsContent>

						{/* Profilo Prestativo Tab */}
						{/* Profilo prestativo: tutti i modelli di uno sport, di sistema e dei
						    coach, nello stesso editor della sezione coach. */}
						<TabsContent value="profilo-prestativo">
							<PerformanceModelsAdminTab />
						</TabsContent>

						{/* Exercise Categories Tab */}
						<TabsContent value="exercise-categories">
							<Card className="bg-card border-border rounded-sm">
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle className="font-heading text-xl flex items-center gap-2">
												<FolderOpen className="w-5 h-5 text-volt" />
												Categorie Esercizi
											</CardTitle>
											<p className="text-sm text-muted-foreground mt-1">
												Gestisci le categorie disponibili nella libreria
												esercizi
											</p>
										</div>
										<Button
											onClick={openNewCategory}
											className="bg-volt hover:bg-volt/90 rounded-sm"
										>
											<Plus className="w-4 h-4 mr-2" />
											Nuova Categoria
										</Button>
									</div>
								</CardHeader>
								<CardContent>
									{exerciseCategories.length > 0 ? (
										<div className="space-y-2">
											{exerciseCategories.map((cat, idx) => {
												const exCount = getExerciseCountForCategory(cat.value);
												return (
													<div
														key={cat.category_id}
														className="flex items-center justify-between p-4 bg-secondary rounded-sm"
													>
														<div className="flex items-center gap-4">
															<div className="w-8 h-8 bg-volt/10 rounded-sm flex items-center justify-center text-volt font-heading text-sm">
																{idx + 1}
															</div>
															<div>
																<p className="font-medium">{cat.label}</p>
																<p className="text-xs text-muted-foreground font-mono">
																	{cat.value}
																</p>
															</div>
														</div>
														<div className="flex items-center gap-3">
															<Badge className="bg-secondary text-muted-foreground border-border">
																{exCount}{" "}
																{exCount === 1 ? "esercizio" : "esercizi"}
															</Badge>
															<Button
																variant="ghost"
																size="icon"
																className="rounded-sm"
																onClick={() => openEditCategory(cat)}
															>
																<Edit className="w-4 h-4" />
															</Button>
															<Button
																variant="ghost"
																size="icon"
																className="rounded-sm hover:text-red-500"
																onClick={() =>
																	handleDeleteCategory(cat.category_id)
																}
																title={
																	exCount > 0
																		? "Riassegna prima gli esercizi"
																		: "Elimina"
																}
															>
																<Trash2 className="w-4 h-4" />
															</Button>
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<div className="text-center py-12 text-muted-foreground">
											<FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
											<p className="font-heading text-lg mb-2">
												Nessuna categoria configurata
											</p>
											<p className="text-sm mb-4">
												Le categorie predefinite verranno create automaticamente
											</p>
											<Button
												onClick={loadData}
												className="bg-volt hover:bg-volt/90 rounded-sm"
											>
												Carica Categorie Default
											</Button>
										</div>
									)}
								</CardContent>
							</Card>
						</TabsContent>
					</div>
				</Tabs>

				{/* AI Training Doc Dialog */}
				<Dialog
					open={isAiDocDialogOpen}
					onOpenChange={(open) => {
						setIsAiDocDialogOpen(open);
						if (!open) {
							setEditingAiDoc(null);
							setAiDocPdfFile(null);
							setAiDocSourceMode("text");
							setAiDocForm({
								title: "",
								content: "",
								category: "methodology",
								is_active: true,
							});
						}
					}}
				>
					<DialogContent className="bg-card border-border max-w-2xl">
						<DialogDescription className="hidden" />
						<DialogHeader>
							<DialogTitle>
								{editingAiDoc
									? "Modifica Documento Formativo"
									: "Nuovo Documento Formativo"}
							</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>Titolo</Label>
								<Input
									value={aiDocForm.title}
									onChange={(e) =>
										setAiDocForm({ ...aiDocForm, title: e.target.value })
									}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="Es: Linee guida periodizzazione forza"
								/>
							</div>
							<div className="space-y-2">
								<Label>Categoria</Label>
								<Select
									value={aiDocForm.category}
									onValueChange={(v) =>
										setAiDocForm({ ...aiDocForm, category: v })
									}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										{aiDocCategories.map((cat) => (
											<SelectItem key={cat.value} value={cat.value}>
												{cat.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							{!editingAiDoc && (
								<div className="grid grid-cols-2 gap-1 rounded-sm bg-secondary p-1">
									<Button
										type="button"
										variant={aiDocSourceMode === "text" ? "default" : "ghost"}
										className="rounded-sm"
										onClick={() => setAiDocSourceMode("text")}
									>
										<FileText className="w-4 h-4 mr-2" />
										Testo
									</Button>
									<Button
										type="button"
										variant={aiDocSourceMode === "pdf" ? "default" : "ghost"}
										className="rounded-sm"
										onClick={() => setAiDocSourceMode("pdf")}
									>
										<Upload className="w-4 h-4 mr-2" />
										Carica PDF
									</Button>
								</div>
							)}
							{aiDocSourceMode === "pdf" && !editingAiDoc ? (
								<div className="space-y-2">
									<Label>File PDF</Label>
									<Input
										type="file"
										accept=".pdf,application/pdf"
										onChange={(event) => {
											const selected = event.target.files?.[0] || null;
											setAiDocPdfFile(selected);
											if (selected && !aiDocForm.title.trim()) {
												setAiDocForm((current) => ({
													...current,
													title: selected.name.replace(/\.pdf$/i, ""),
												}));
											}
										}}
										className="bg-secondary border-transparent rounded-sm"
									/>
									<p className="text-xs text-muted-foreground">
										{aiDocPdfFile?.name ||
											"PDF testuale, massimo 15 MB. Le scansioni non sono supportate."}
									</p>
								</div>
							) : (
								<div className="space-y-2">
									<Label>Contenuto</Label>
									<Textarea
										value={aiDocForm.content}
										onChange={(e) =>
											setAiDocForm({ ...aiDocForm, content: e.target.value })
										}
										className="bg-secondary border-transparent rounded-sm min-h-[200px] resize-y"
										placeholder="Inserisci il contenuto del documento formativo."
									/>
									<p className="text-xs text-muted-foreground">
										{aiDocForm.content.length} caratteri
									</p>
								</div>
							)}
							<Button
								onClick={handleSaveAiDoc}
								className="w-full bg-volt hover:bg-volt/90 rounded-sm"
								disabled={isSavingAiDoc}
							>
								{isSavingAiDoc
									? "Salvataggio..."
									: editingAiDoc
										? "Salva Modifiche"
										: "Crea Documento"}
							</Button>
						</div>
					</DialogContent>
				</Dialog>

				{/* Edit User Dialog */}
				<Dialog open={isUserEditOpen} onOpenChange={setIsUserEditOpen}>
					<DialogContent className="bg-card border-border">
						<DialogDescription className="hidden" />
						<DialogHeader>
							<DialogTitle>Modifica Utente</DialogTitle>
						</DialogHeader>
						{selectedUser && (
							<div className="space-y-4">
								<div>
									<p className="font-medium">{selectedUser.name}</p>
									<p className="text-sm text-muted-foreground">
										{selectedUser.email}
									</p>
								</div>
								<div className="space-y-2">
									<Label>Ruolo</Label>
									<Select
										value={selectedUser.role}
										onValueChange={(v) =>
											setSelectedUser({ ...selectedUser, role: v })
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="athlete">Atleta</SelectItem>
											<SelectItem value="coach">Coach</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{!selectedUser.is_admin && (
									<div className="space-y-2">
										<div className="flex items-center justify-between rounded-sm border border-border bg-secondary/50 px-3 py-2">
											<div className="min-w-0 pr-3">
												<p className="text-sm">Abilita anche come Coach</p>
												<p className="text-xs text-muted-foreground">
													Un solo account che accede sia alle funzioni coach sia
													al profilo atleta, con lo switch di modalità nel menu
													utente.
												</p>
											</div>
											<Switch
												checked={!!selectedUser.coach_enabled}
												onCheckedChange={(checked) =>
													setSelectedUser({
														...selectedUser,
														coach_enabled: checked,
														// Canonical dual shape: base role stays "athlete".
														role: checked ? "athlete" : selectedUser.role,
													})
												}
												data-testid="user-coach-enabled-toggle"
											/>
										</div>
									</div>
								)}
								<div className="space-y-2">
									<Label>Team</Label>
									<Select
										value={selectedUser.team_id || "none"}
										onValueChange={(v) =>
											setSelectedUser({
												...selectedUser,
												team_id: v === "none" ? null : v,
											})
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue placeholder="Nessun team" />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="none">Nessun team</SelectItem>
											{teams.map((t) => (
												<SelectItem key={t.team_id} value={t.team_id}>
													{t.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>Account collegati</Label>
									<div className="flex flex-wrap gap-2">
										{Array.isArray(selectedUser.linked_account_ids) &&
										selectedUser.linked_account_ids.length > 0 ? (
											selectedUser.linked_account_ids.map((linkedId) => {
												const linkedUser = users.find(
													(u) => u.user_id === linkedId,
												);
												if (!linkedUser) return null;
												return (
													<Badge
														key={linkedId}
														variant="outline"
														className="rounded-sm flex items-center gap-1"
													>
														<span className="truncate max-w-[180px]">
															{linkedUser.name || linkedUser.email}
														</span>
														<button
															type="button"
															onClick={() =>
																setSelectedUser({
																	...selectedUser,
																	linked_account_ids: (
																		selectedUser.linked_account_ids || []
																	).filter((id) => id !== linkedId),
																})
															}
															className="text-muted-foreground hover:text-foreground"
														>
															<X className="w-3.5 h-3.5" />
														</button>
													</Badge>
												);
											})
										) : (
											<p className="text-xs text-muted-foreground">
												Nessun account collegato
											</p>
										)}
									</div>
									<div className="flex gap-2">
										<Select
											value={selectedLinkedAccountId}
											onValueChange={setSelectedLinkedAccountId}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm">
												<SelectValue placeholder="Seleziona account" />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												<SelectItem value="none">Seleziona account</SelectItem>
												{linkableAccounts.map((candidate) => (
													<SelectItem
														key={candidate.user_id}
														value={candidate.user_id}
													>
														{candidate.name || candidate.email} (
														{candidate.is_admin ? "admin" : candidate.role})
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<Button
											type="button"
											variant="outline"
											className="rounded-sm"
											onClick={() => {
												if (
													!selectedLinkedAccountId ||
													selectedLinkedAccountId === "none"
												)
													return;
												const alreadyLinked = new Set(
													selectedUser.linked_account_ids || [],
												);
												if (alreadyLinked.has(selectedLinkedAccountId)) return;
												setSelectedUser({
													...selectedUser,
													linked_account_ids: [
														...(selectedUser.linked_account_ids || []),
														selectedLinkedAccountId,
													],
												});
												setSelectedLinkedAccountId("none");
											}}
										>
											<Plus className="w-4 h-4" />
										</Button>
									</div>
								</div>
								<Button
									onClick={handleUpdateUser}
									className="w-full bg-volt hover:bg-volt/90 rounded-sm"
								>
									Salva Modifiche
								</Button>
							</div>
						)}
					</DialogContent>
				</Dialog>

				{/* Share Program Dialog */}
				<Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
					<DialogContent className="bg-card border-border">
						<DialogDescription className="hidden" />
						<DialogHeader>
							<DialogTitle>Condividi Programma</DialogTitle>
						</DialogHeader>
						{selectedProgram && (
							<div className="space-y-4">
								<div>
									<p className="font-medium">{selectedProgram.title}</p>
									<p className="text-sm text-muted-foreground">
										Seleziona il coach con cui condividere questo programma
									</p>
								</div>
								<div className="space-y-2">
									{coaches
										.filter((c) => c.user_id !== selectedProgram.coach_id)
										.map((coach) => (
											<Button
												key={coach.user_id}
												variant="outline"
												className="w-full justify-start rounded-sm"
												onClick={() => handleShareProgram(coach.user_id)}
											>
												<div className="w-8 h-8 bg-acid/20 rounded-full flex items-center justify-center mr-3">
													{coach.name?.charAt(0)}
												</div>
												<div className="text-left">
													<p>{coach.name}</p>
													<p className="text-xs text-muted-foreground">
														{coach.email}
													</p>
												</div>
											</Button>
										))}
								</div>
							</div>
						)}
					</DialogContent>
				</Dialog>
			</div>

			{/* Exercise Category Dialog */}
			<Dialog
				open={isCategoryDialogOpen}
				onOpenChange={(open) => {
					setIsCategoryDialogOpen(open);
					if (!open) {
						setEditingCategory(null);
						setCategoryForm({ value: "", label: "", order: 0 });
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle>
							{editingCategory ? "Modifica Categoria" : "Nuova Categoria"}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>Etichetta *</Label>
							<Input
								value={categoryForm.label}
								onChange={(e) =>
									setCategoryForm({
										...categoryForm,
										label: e.target.value,
										...(!editingCategory && {
											value: e.target.value
												.toLowerCase()
												.replace(/[^a-z0-9]+/g, "_")
												.replace(/^_|_$/g, ""),
										}),
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="Es: Mobilità"
							/>
						</div>
						<div className="space-y-2">
							<Label>Valore (ID) *</Label>
							<Input
								value={categoryForm.value}
								onChange={(e) =>
									setCategoryForm({
										...categoryForm,
										value: e.target.value
											.toLowerCase()
											.replace(/[^a-z0-9_]+/g, ""),
									})
								}
								className="bg-secondary border-transparent rounded-sm font-mono text-sm"
								placeholder="Es: mobility"
							/>
							<p className="text-xs text-muted-foreground">
								Identificativo univoco, solo lettere minuscole, numeri e
								underscore
							</p>
						</div>
						<Button
							onClick={handleSaveCategory}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							{editingCategory ? "Salva Modifiche" : "Crea Categoria"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

		</Layout>
	);
}
